import { useEffect, useRef, useState } from "react";
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { valueOrNull } from "@/shared/value";
import { useMoveCompanyDocumentNode } from "@/modules/documents/features/move-company-document-node";
import { resolveDropTarget } from "../utils/resolve-drop-target";
import { ancestorsOf } from "../utils/ancestors-of";
import { isFolderNode } from "../utils/tree-node-guards";
import { CompanyDocumentTreeNode } from "./types";

/** Сколько держать узел над свёрнутой папкой, чтобы она раскрылась. */
const EXPAND_ON_HOVER_DELAY_MS = 600;

export function useCompanyDocumentTreeDnd({
  nodeById,
  expand,
}: {
  nodeById: Map<string, CompanyDocumentTreeNode>;
  /** Раскрыть папку: во время перетаскивания стрелку не нажать. */
  expand: (key: string) => void;
}) {
  const { moveFolderTo, moveDocumentTo, loading } =
    useMoveCompanyDocumentNode();
  const [activeId, setActiveId] = useState<string>();
  const [overId, setOverId] = useState<string>();
  const expandTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelExpand = () => clearTimeout(expandTimer.current);

  useEffect(() => cancelExpand, []);

  const activeNode = activeId ? nodeById.get(activeId) : undefined;
  const dropTarget =
    activeNode && overId
      ? resolveDropTarget(activeNode, overId, nodeById)
      : undefined;

  const reset = () => {
    cancelExpand();
    setActiveId(undefined);
    setOverId(undefined);
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const onDragOver = (event: DragOverEvent) => {
    const id = event.over ? String(event.over.id) : undefined;
    const over = id ? nodeById.get(id) : undefined;

    setOverId(id);
    cancelExpand();

    if (over && isFolderNode(over)) {
      expandTimer.current = setTimeout(
        () => expand(over.id),
        EXPAND_ON_HOVER_DELAY_MS,
      );
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    reset();

    const dragged = nodeById.get(String(event.active.id));
    const target =
      dragged && event.over
        ? resolveDropTarget(dragged, String(event.over.id), nodeById)
        : undefined;

    if (!dragged || !target) {
      return;
    }

    // Скрытый узел перетаскивать некуда: его идентификатор до клиента не доехал.
    if (isFolderNode(dragged)) {
      const folderId = valueOrNull(dragged.folder)?.id;

      if (folderId) {
        await moveFolderTo(folderId, target.folderId);
      }

      return;
    }

    const documentId = valueOrNull(dragged.document)?.id;

    if (documentId) {
      await moveDocumentTo(documentId, target.folderId);
    }
  };

  return {
    /** Путь перетаскиваемого узла — уровни, на которые его можно поднять. */
    activeAncestors: activeNode ? ancestorsOf(activeNode, nodeById) : [],
    dropTargetKey: dropTarget?.key,
    moving: loading,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel: reset,
  };
}
